/**
 * Stage 2.1 — Service Dependency Registry
 *
 * Central config mapping each registered service to its full dependency
 * footprint: transition policies, affected UI, mutations, pipeline stages,
 * in-flight handling, and messaging families.
 *
 * This registry is the single source of truth for the governance framework.
 * It is consumed by useServiceGovernance, page guards, and backend guards.
 */

export type TransitionPolicy = 'hard_stop' | 'graceful_wind_down' | 'freeze' | 'read_only';

export type InFlightAction = 'block' | 'cancel' | 'allow_completion' | 'preserve_read_only';

export interface InFlightStatePolicy {
  state: string;
  action: InFlightAction;
  description: string;
}

export interface ServiceDependency {
  label: string;
  platform: 'hire' | 'seek' | 'both';
  globalOffPolicy: TransitionPolicy;
  planOffPolicy: TransitionPolicy;
  routes: string[];
  pipelineStagesAffected: string[];
  /** Edge functions / RPCs directly blocked when OFF */
  mutations: string[];
  /** Edge functions with governance pre-checks (conditional, not full block) */
  callerGuards: string[];
  inhouseAiProductionBlock?: {
    status: 'inhouse_required';
    outsourcedAiPersonalDataAllowed: false;
    capabilityLabels: string[];
  };
  inFlight: {
    notStarted: 'cancel' | 'freeze' | 'allow';
    inProgress: 'allow_completion' | 'freeze' | 'cancel';
    historical: 'read_only' | 'hidden';
  };
  /** Formal in-flight state classifications */
  inFlightStates: InFlightStatePolicy[];
  messages: {
    globalOff: string;
    planOff: string;
    transitionImpact: string;
    readOnly: string;
  };
}

export const SERVICE_DEPENDENCY_REGISTRY: Record<string, ServiceDependency> = {
  ai_interviews: {
    label: 'AI Interviews',
    platform: 'hire',
    globalOffPolicy: 'graceful_wind_down',
    planOffPolicy: 'graceful_wind_down',
    routes: ['/interviews', '/interviews/:id'],
    pipelineStagesAffected: ['interviewed'],
    mutations: ['score-interview'],
    callerGuards: ['upload-interview-video', 'send-candidate-notification-email', 'advance-stage'],
    inhouseAiProductionBlock: {
      status: 'inhouse_required',
      outsourcedAiPersonalDataAllowed: false,
      capabilityLabels: ['forkhire_interview_stt_transcription', 'forkhire_transcript_interview_scoring'],
    },
    inFlight: { notStarted: 'cancel', inProgress: 'allow_completion', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new interview sessions can be created' },
      { state: 'invited', action: 'block', description: 'Pending invitations are cancelled; new invitations blocked' },
      { state: 'link_opened', action: 'allow_completion', description: 'Candidate opened link before disable; session may complete' },
      { state: 'in_progress', action: 'allow_completion', description: 'Active recording session allowed to finish and submit' },
      { state: 'submitted_awaiting_scoring', action: 'allow_completion', description: 'Submitted responses are still scored' },
      { state: 'completed', action: 'preserve_read_only', description: 'Historical scores and recordings remain visible read-only' },
    ],
    messages: {
      globalOff: 'AI Interviews are currently disabled platform-wide.',
      planOff: 'AI Interviews are not included in your current plan.',
      transitionImpact: 'Pending interview invitations were cancelled. In-progress sessions were allowed to complete.',
      readOnly: 'Interview results are preserved as read-only and cannot be modified.',
    },
  },

  ai_cv_scoring: {
    label: 'AI CV Scoring',
    platform: 'hire',
    globalOffPolicy: 'graceful_wind_down',
    planOffPolicy: 'graceful_wind_down',
    routes: [],
    pipelineStagesAffected: ['ai_reviewed'],
    mutations: ['score-cv', 'parse-cv'],
    callerGuards: ['seek-apply', 'accept-invite', 'submit-application'],
    inhouseAiProductionBlock: {
      status: 'inhouse_required',
      outsourcedAiPersonalDataAllowed: false,
      capabilityLabels: ['forkhire_cv_ocr_parsing', 'forkhire_cv_scoring', 'shared_auto_trigger_protection'],
    },
    inFlight: { notStarted: 'cancel', inProgress: 'allow_completion', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new CV scoring jobs can be initiated' },
      { state: 'queued', action: 'cancel', description: 'Pending scoring jobs in queue are cancelled' },
      { state: 'in_progress', action: 'allow_completion', description: 'Currently running AI scoring completes' },
      { state: 'completed', action: 'preserve_read_only', description: 'Historical scores remain visible read-only' },
    ],
    messages: {
      globalOff: 'AI CV Scoring is currently disabled platform-wide.',
      planOff: 'AI CV Scoring is not included in your current plan.',
      transitionImpact: 'Pending CV scores were cancelled. Existing scores are preserved.',
      readOnly: 'CV scores are preserved as read-only.',
    },
  },

  job_management: {
    label: 'Job Management',
    platform: 'hire',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: ['/jobs', '/jobs/:id'],
    pipelineStagesAffected: [],
    mutations: ['generate-job-details'],
    callerGuards: ['generate-job-details'],
    inFlight: { notStarted: 'freeze', inProgress: 'freeze', historical: 'read_only' },
    inFlightStates: [
      { state: 'draft', action: 'cancel', description: 'Draft jobs are closed — cannot be published while service is OFF' },
      { state: 'open', action: 'preserve_read_only', description: 'Open jobs frozen in place — visible but no new applications accepted' },
      { state: 'published', action: 'preserve_read_only', description: 'Published jobs frozen — remain visible, mutations blocked' },
      { state: 'closed', action: 'preserve_read_only', description: 'Already closed jobs — no action needed' },
      { state: 'filled', action: 'preserve_read_only', description: 'Filled jobs — no action needed' },
    ],
    messages: {
      globalOff: 'Job Management is currently disabled platform-wide.',
      planOff: 'Job Management is not included in your current plan.',
      transitionImpact: 'Draft jobs were closed. Open jobs are frozen. Historical data is preserved.',
      readOnly: 'Jobs are preserved as read-only and cannot be modified.',
    },
  },

  candidate_pipeline: {
    label: 'Candidate Pipeline',
    platform: 'hire',
    globalOffPolicy: 'freeze',
    planOffPolicy: 'freeze',
    routes: ['/candidates', '/candidates/:id'],
    pipelineStagesAffected: [],
    mutations: ['advance-stage', 'backfill-candidate'],
    callerGuards: [],
    inFlight: { notStarted: 'freeze', inProgress: 'freeze', historical: 'read_only' },
    inFlightStates: [
      { state: 'applied', action: 'preserve_read_only', description: 'Applied candidates frozen — no stage advancement' },
      { state: 'ai_reviewed', action: 'preserve_read_only', description: 'AI-reviewed candidates frozen in place' },
      { state: 'shortlisted', action: 'preserve_read_only', description: 'Shortlisted candidates frozen — no further action' },
      { state: 'interviewed', action: 'preserve_read_only', description: 'Interviewed candidates frozen in place' },
      { state: 'f2f_interview', action: 'preserve_read_only', description: 'F2F interview candidates frozen' },
      { state: 'offering', action: 'preserve_read_only', description: 'Offering-stage frozen — sent offers allowed to complete' },
      { state: 'sent_offers', action: 'allow_completion', description: 'Already-sent offers can still be accepted/declined' },
      { state: 'hired', action: 'preserve_read_only', description: 'Terminal state — no action' },
      { state: 'rejected', action: 'preserve_read_only', description: 'Terminal state — no action' },
    ],
    messages: {
      globalOff: 'Candidate Pipeline is currently disabled platform-wide.',
      planOff: 'Candidate Pipeline is not included in your current plan.',
      transitionImpact: 'Pipeline interactions are frozen. Sent offers can still be accepted. Historical applications remain visible.',
      readOnly: 'Pipeline data is preserved as read-only.',
    },
  },

  talent_pool: {
    label: 'Talent Discovery',
    platform: 'hire',
    globalOffPolicy: 'graceful_wind_down',
    planOffPolicy: 'graceful_wind_down',
    routes: ['/talent', '/talent/invitations'],
    pipelineStagesAffected: [],
    mutations: ['talent-search', 'send-invite', 'accept-invite', 'decline-invite', 'withdraw-invite'],
    callerGuards: ['send-invite', 'withdraw-invite'],
    inFlight: { notStarted: 'cancel', inProgress: 'freeze', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new talent searches or invitations can be initiated' },
      { state: 'pending', action: 'cancel', description: 'Pending invitations are auto-withdrawn' },
      { state: 're_invited', action: 'cancel', description: 'Re-invited invitations are auto-withdrawn' },
      { state: 'accepted', action: 'preserve_read_only', description: 'Accepted invites preserved — applications already created' },
      { state: 'declined', action: 'preserve_read_only', description: 'Declined invites preserved as historical records' },
      { state: 'withdrawn', action: 'preserve_read_only', description: 'Already withdrawn invites remain unchanged' },
    ],
    messages: {
      globalOff: 'Talent Discovery is currently disabled platform-wide.',
      planOff: 'Talent Discovery is not included in your current plan.',
      transitionImpact: 'Pending invites were withdrawn. Historical invite records are preserved.',
      readOnly: 'Discovery data is preserved as read-only.',
    },
  },

  analytics: {
    label: 'Analytics & Reports',
    platform: 'hire',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: ['/analytics'],
    pipelineStagesAffected: [],
    mutations: [],
    callerGuards: [],
    inFlight: { notStarted: 'cancel', inProgress: 'cancel', historical: 'read_only' },
    inFlightStates: [],
    messages: {
      globalOff: 'Analytics & Reports are currently disabled platform-wide.',
      planOff: 'Analytics & Reports are not included in your current plan.',
      transitionImpact: 'Reporting is unavailable.',
      readOnly: 'Historical reports are preserved as read-only.',
    },
  },

  team_management: {
    label: 'Team Management',
    platform: 'hire',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: ['/team'],
    pipelineStagesAffected: [],
    mutations: ['manage-team', 'invite-admin'],
    callerGuards: [],
    inFlight: { notStarted: 'cancel', inProgress: 'freeze', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new team invitations can be sent' },
      { state: 'pending', action: 'cancel', description: 'Pending team invites are suspended' },
      { state: 'invited', action: 'cancel', description: 'Outstanding invited members are suspended' },
      { state: 'active', action: 'preserve_read_only', description: 'Active members remain but cannot be managed — roles and permissions frozen' },
      { state: 'role_assignments', action: 'preserve_read_only', description: 'Existing role assignments preserved read-only' },
    ],
    messages: {
      globalOff: 'Team Management is currently disabled platform-wide.',
      planOff: 'Team Management is not included in your current plan.',
      transitionImpact: 'Pending team invites were suspended. Existing members remain with frozen roles.',
      readOnly: 'Team data is preserved as read-only.',
    },
  },

  ai_job_generator: {
    label: 'AI Job Generator',
    platform: 'hire',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: [],
    pipelineStagesAffected: [],
    mutations: ['generate-job-details'],
    callerGuards: [],
    inhouseAiProductionBlock: {
      status: 'inhouse_required',
      outsourcedAiPersonalDataAllowed: false,
      capabilityLabels: ['forkhire_job_detail_generation'],
    },
    inFlight: { notStarted: 'cancel', inProgress: 'cancel', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new AI generation requests can be initiated' },
      { state: 'in_progress', action: 'block', description: 'Stateless service — no persistent in-progress state exists' },
      { state: 'historical', action: 'preserve_read_only', description: 'Previously generated content preserved inside job records' },
    ],
    messages: {
      globalOff: 'AI Job Generator is currently disabled platform-wide.',
      planOff: 'AI Job Generator is not included in your current plan.',
      transitionImpact: 'AI job generation is unavailable. Manual creation remains available. Previously generated content is preserved.',
      readOnly: 'Previously generated content is preserved.',
    },
  },

  interview_retakes: {
    label: 'Interview Retakes',
    platform: 'hire',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: [],
    pipelineStagesAffected: [],
    mutations: [],
    callerGuards: [],
    inFlight: { notStarted: 'cancel', inProgress: 'freeze', historical: 'read_only' },
    inFlightStates: [],
    messages: {
      globalOff: 'Interview Retakes are currently disabled platform-wide.',
      planOff: 'Interview Retakes are not included in your current plan.',
      transitionImpact: 'No new retakes allowed.',
      readOnly: 'Existing retake results are preserved.',
    },
  },

  job_discovery: {
    label: 'Job Discovery',
    platform: 'seek',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: ['/seek/jobs', '/seek/saved-jobs'],
    pipelineStagesAffected: [],
    mutations: ['match-jobs'],
    callerGuards: ['match-jobs'],
    inFlight: { notStarted: 'cancel', inProgress: 'cancel', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new job searches or match computations can be initiated' },
      { state: 'saved_jobs', action: 'preserve_read_only', description: 'Saved job bookmarks preserved — visible but no new saves' },
      { state: 'match_scores', action: 'preserve_read_only', description: 'Historical match scores preserved as read-only' },
      { state: 'existing_applications', action: 'allow_completion', description: 'Already-submitted applications unaffected' },
    ],
    messages: {
      globalOff: 'Job Discovery is currently disabled platform-wide.',
      planOff: 'Job Discovery is not included in your current plan.',
      transitionImpact: 'Job browsing and matching is unavailable.',
      readOnly: 'Saved jobs are preserved as read-only.',
    },
  },

  interview_practice: {
    label: 'Interview Practice',
    platform: 'seek',
    globalOffPolicy: 'graceful_wind_down',
    planOffPolicy: 'graceful_wind_down',
    routes: ['/seek/interview-practice', '/seek/practice-session'],
    pipelineStagesAffected: [],
    mutations: ['generate-practice-questions', 'score-practice-response'],
    callerGuards: [],
    inhouseAiProductionBlock: {
      status: 'inhouse_required',
      outsourcedAiPersonalDataAllowed: false,
      capabilityLabels: ['forkseek_practice_question_generation', 'forkseek_practice_stt_transcription'],
    },
    inFlight: { notStarted: 'cancel', inProgress: 'allow_completion', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new practice sessions can be created' },
      { state: 'pending', action: 'cancel', description: 'Pending sessions cancelled' },
      { state: 'in_progress', action: 'allow_completion', description: 'Active practice sessions allowed to complete' },
      { state: 'completed', action: 'preserve_read_only', description: 'Completed sessions with scores preserved read-only' },
      { state: 'cancelled', action: 'preserve_read_only', description: 'Cancelled sessions remain as historical records' },
    ],
    messages: {
      globalOff: 'Interview Practice is currently disabled platform-wide.',
      planOff: 'Interview Practice is not included in your current plan.',
      transitionImpact: 'Pending practice sessions were cancelled. In-progress sessions may complete.',
      readOnly: 'Practice results are preserved as read-only.',
    },
  },

  cv_builder: {
    label: 'CV Builder',
    platform: 'seek',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: ['/seek/cv'],
    pipelineStagesAffected: [],
    mutations: ['generate-cv', 'export-cv'],
    callerGuards: ['export-cv'],
    inhouseAiProductionBlock: {
      status: 'inhouse_required',
      outsourcedAiPersonalDataAllowed: false,
      capabilityLabels: ['forkseek_cv_review_generation'],
    },
    inFlight: { notStarted: 'cancel', inProgress: 'cancel', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new CV versions can be generated' },
      { state: 'existing_versions', action: 'preserve_read_only', description: 'All existing CV versions preserved' },
      { state: 'active_reference', action: 'preserve_read_only', description: 'Active CV version reference maintained' },
      { state: 'generation', action: 'block', description: 'AI-powered CV generation blocked' },
      { state: 'export', action: 'block', description: 'CV export blocked while service is OFF' },
    ],
    messages: {
      globalOff: 'CV Builder is currently disabled platform-wide.',
      planOff: 'CV Builder is not included in your current plan.',
      transitionImpact: 'CV generation is unavailable. Existing versions are preserved.',
      readOnly: 'Existing CV versions are preserved as read-only.',
    },
  },

  cv_review: {
    label: 'CV Review',
    platform: 'seek',
    globalOffPolicy: 'hard_stop',
    planOffPolicy: 'hard_stop',
    routes: [],
    pipelineStagesAffected: [],
    mutations: ['review-cv'],
    callerGuards: [],
    inhouseAiProductionBlock: {
      status: 'inhouse_required',
      outsourcedAiPersonalDataAllowed: false,
      capabilityLabels: ['forkseek_cv_review_generation'],
    },
    inFlight: { notStarted: 'cancel', inProgress: 'cancel', historical: 'read_only' },
    inFlightStates: [
      { state: 'not_created', action: 'block', description: 'No new CV review requests can be initiated' },
      { state: 'pending', action: 'cancel', description: 'Pending review requests cancelled' },
      { state: 'processing', action: 'allow_completion', description: 'Currently processing reviews allowed to complete' },
      { state: 'completed', action: 'preserve_read_only', description: 'Completed review results preserved read-only' },
      { state: 'approved', action: 'preserve_read_only', description: 'Approved suggestions preserved' },
      { state: 'applied_suggestions', action: 'preserve_read_only', description: 'AI suggestions already applied remain intact' },
    ],
    messages: {
      globalOff: 'CV Review is currently disabled platform-wide.',
      planOff: 'CV Review is not included in your current plan.',
      transitionImpact: 'No new reviews can be requested.',
      readOnly: 'Existing review results are preserved as read-only.',
    },
  },
};

/** Get dependency config for a service, or undefined if unregistered */
export function getServiceDependency(serviceKey: string): ServiceDependency | undefined {
  return SERVICE_DEPENDENCY_REGISTRY[serviceKey];
}

/** Get the transition policy to apply for a given service and block reason */
export function getTransitionPolicy(
  serviceKey: string,
  blockReason: 'service_globally_disabled' | 'plan_not_entitled' | 'backend_blocked',
): TransitionPolicy | undefined {
  const dep = SERVICE_DEPENDENCY_REGISTRY[serviceKey];
  if (!dep) return undefined;
  if (blockReason === 'backend_blocked') return 'hard_stop';
  return blockReason === 'service_globally_disabled' ? dep.globalOffPolicy : dep.planOffPolicy;
}

/** Get the appropriate user-facing message for a service governance state */
export function getGovernanceMessage(
  serviceKey: string,
  blockReason: 'service_globally_disabled' | 'plan_not_entitled' | 'backend_blocked' | null,
): string | null {
  const dep = SERVICE_DEPENDENCY_REGISTRY[serviceKey];
  if (!dep || !blockReason) return null;
  if (blockReason === 'backend_blocked') {
    return 'Subscription access could not be verified. Please try again or contact support.';
  }
  return blockReason === 'service_globally_disabled'
    ? dep.messages.globalOff
    : dep.messages.planOff;
}
